import React, { useCallback, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import Langs, { Localize } from "../../../app/lang/langs";
import NetworkManager from "../../../app/managers/networkManager";
import { vlearnSurface, vlearnType } from "../vlearnUi";

/**
 * V-Learning — a lesson whose content is a video uploaded to the platform.
 *
 * The native `<video controls>` element, deliberately: it is keyboard-operable, screen-reader
 * labelled and captioned by the browser for free, and no player library this project would
 * have to add does any of that better for a single MP4.
 */

// How many seconds of watching accumulate before the parent is told. `timeupdate` fires
// roughly four times a second, and dispatching a request on each one would be several
// hundred writes for a ten-minute video — so the deltas are accumulated in a ref and
// flushed on a threshold instead.
const REPORT_EVERY_SECONDS = 5

// Anything larger than this between two `timeupdate` events is a seek, not watching, and
// must not be counted as time spent.
const MAX_PLAUSIBLE_DELTA = 4

// Never resume within this many seconds of the end: dropping someone back onto the closing
// frame of a video they had all but finished is worse than starting it again.
const RESUME_TAIL_GUARD = 10

export default function VideoUploadLesson({ lesson, onProgress }) {

    const files = (lesson && lesson.files) || []
    const videoFile = files.find(file => file.kind === 'video') || null
    const captionFiles = files.filter(file => file.kind === 'caption')

    const videoRef = useRef(null)
    /** Seconds watched but not yet reported. */
    const pendingRef = useRef(0)
    /** The playhead at the previous `timeupdate`, to derive a delta from. */
    const lastTimeRef = useRef(0)
    const progressRef = useRef(onProgress)
    progressRef.current = onProgress

    const flush = useCallback(() => {
        const seconds = Math.round(pendingRef.current)
        if (seconds < 1) return
        pendingRef.current = 0
        if (progressRef.current) progressRef.current(seconds, lastTimeRef.current)
    }, [])

    // Flushing on unmount matters more than it looks: a learner who watches four minutes and
    // then clicks the next lesson would otherwise lose everything since the last threshold.
    useEffect(() => flush, [flush])

    const onLoadedMetadata = useCallback(() => {
        const video = videoRef.current
        if (!video) return
        const resumeAt = Number(lesson && lesson.last_position_seconds) || 0
        const duration = Number(video.duration) || 0
        if (resumeAt > 0 && duration > 0 && resumeAt < duration - RESUME_TAIL_GUARD) {
            video.currentTime = resumeAt
            lastTimeRef.current = resumeAt
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lesson && lesson.id])

    const onTimeUpdate = useCallback(() => {
        const video = videoRef.current
        if (!video) return
        const current = Number(video.currentTime) || 0
        const delta = current - lastTimeRef.current
        lastTimeRef.current = current
        if (delta > 0 && delta <= MAX_PLAUSIBLE_DELTA) pendingRef.current += delta
        if (pendingRef.current >= REPORT_EVERY_SECONDS) flush()
    }, [flush])

    if (!videoFile) {
        return (
            <Box sx={{ ...vlearnSurface.panel }}>
                <Typography sx={{ ...vlearnType.body }}>
                    <Langs str="VLEARN_VIDEO_MISSING" />
                </Typography>
            </Box>
        )
    }

    const network = NetworkManager.getInstance()

    return (
        <Box>
            <Box sx={{
                ...vlearnSurface.card,
                backgroundColor: '#000',
                '& video': { display: 'block', width: '100%', maxHeight: '68vh', outline: 'none' },
                '& video:focus-visible': {
                    outline: '2px solid var(--orange, rgb(255,144,21))',
                    outlineOffset: '2px'
                }
            }}>
                <video
                    ref={videoRef}
                    controls
                    preload="metadata"
                    playsInline
                    src={network.getVLearnStreamUrl(videoFile.id)}
                    aria-label={lesson.title || ''}
                    onLoadedMetadata={onLoadedMetadata}
                    onTimeUpdate={onTimeUpdate}
                    onPause={flush}
                    onEnded={flush}
                >
                    {/* Caption tracks are fetched by the browser from the API origin, which is
                        not this page's origin. Setting `crossOrigin` on the video would make
                        that fetch a CORS request — but it would also turn the video request
                        itself into one, and a missing header would then stop playback outright
                        rather than only the subtitles. Captions failing quietly is the better
                        of the two failures, so the attribute is deliberately absent. */}
                    {captionFiles.map((file, index) => (
                        <track
                            key={file.id}
                            kind="captions"
                            src={network.getVLearnFileUrl(file.id)}
                            // No language column exists on a lesson file, so the code is read
                            // from the filename ("intro.fr.vtt") and falls back to French,
                            // which is this platform's default locale.
                            srcLang={captionLanguage(file.original_name)}
                            label={file.original_name || ''}
                            default={index === 0}
                        />
                    ))}
                    {Localize('VLEARN_VIDEO_NO_SUPPORT')}
                </video>
            </Box>

            {captionFiles.length ? (
                <Typography sx={{ ...vlearnType.meta, mt: 1 }}>
                    <Langs str="VLEARN_CAPTIONS_AVAILABLE" />
                </Typography>
            ) : null}
        </Box>
    )
}

/** Reads a two-letter language code out of a caption filename, defaulting to French. */
function captionLanguage(originalName) {
    const match = String(originalName || '').match(/[._-]([a-z]{2})\.(vtt|srt)$/i)
    return match ? match[1].toLowerCase() : 'fr'
}
